/*----------------------------------
- HOOK: PREVIEW DRAWER OPENER
----------------------------------*/

/**
 * @docs docs/features/product-domain-listings
 * @docs docs/features/auctions
 * @adr  ADR-0028
 * @rule The control that opened a public preview drawer gets the focus back when the drawer closes,
 *       except through the call to action, which hands the focus to the auth modal it opens. One
 *       hook for every public preview drawer (`/domains*`, Home, `/auctions`).
 */

/*----------------------------------
- DEPENDANCES
----------------------------------*/

import React from 'react';

// Types
import type { PublicPreviewDrawerCloseReason } from '../components/drawer/PublicPreviewDrawerFrame';

/*----------------------------------
- HOOK
----------------------------------*/

const usePreviewDrawerOpener = () => {
    /** The row control that opened the drawer, focused again when it closes. */
    const openerRef = React.useRef<HTMLElement | null>(null);

    /** Called on a row click: the focused element is the row's control. */
    const rememberOpener = React.useCallback(() => {
        openerRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    }, []);

    /** Called on every close; walking to a neighbour keeps the opener. */
    const returnFocus = React.useCallback((reason: PublicPreviewDrawerCloseReason) => {
        // The CTA hands focus to the auth modal it opens; every other close returns it to the row.
        if (reason !== 'cta') openerRef.current?.focus();
        openerRef.current = null;
    }, []);

    return { rememberOpener, returnFocus };
};

export default usePreviewDrawerOpener;
